import { QEFile } from '../QuoteExperienceQuickSetup.page';

export type FileTreeNode = {
    name: string;
    fullPath: string;
    isFolder: boolean;
    children: FileTreeNode[];
    isPending?: boolean;
    isVirtual?: boolean;
};

export const ALLOWED_FILE_ACCEPT = '.svg,.png,.ico,.json,.css';
export const ALLOWED_IMAGE_FILE_MIME_TYPES = [
    'image/svg+xml', 'image/png', 'image/x-icon',
    'application/json'
];
export const ALLOWED_FILE_MIME_TYPES = [
    ...ALLOWED_IMAGE_FILE_MIME_TYPES, 'text/css'
];
export const ALLOWED_FILE_EXTENSIONS_LABEL = 'SVG, PNG, ICO, JSON, CSS';

export const generateId = () => Math.random().toString(36).slice(2);

export const sanitizeName = (s: string) =>
    String(s || '').trim()
        .toLowerCase()
        .replace(/\s+/g, '-')
        .replace(/[^a-z0-9._-]/g, '')
        .replace(/^-+|-+$/g, '');

export function buildFileTree(
    existingPaths: string[],
    pendingFiles: QEFile[],
    virtualFolders: string[]
): FileTreeNode[] {
    const root: FileTreeNode = { name: '', fullPath: '', isFolder: true, children: [] };

    const insertPath = (path: string, isFolder: boolean, isPending = false, isVirtual = false) => {
        const parts = path.split('/').filter(Boolean);
        let current = root;
        for (let i = 0; i < parts.length; i++) {
            const isLast = i === parts.length - 1;
            let child = current.children.find(c => c.name === parts[i]);
            if (!child) {
                child = {
                    name: parts[i],
                    fullPath: parts.slice(0, i + 1).join('/'),
                    isFolder: isLast ? isFolder : true,
                    children: [],
                    isPending: isLast ? isPending : false,
                    isVirtual: isLast ? isVirtual : false,
                };
                current.children.push(child);
            }
            if (isLast && isPending) child.isPending = true;
            if (isLast && isVirtual) child.isVirtual = true;
            current = child;
        }
    };

    existingPaths.forEach(p => insertPath(p, false));
    pendingFiles.forEach(f => { if (f.path) insertPath(f.path, false, true); });
    virtualFolders.forEach(f => insertPath(f, true, false, true));

    const sortNodes = (nodes: FileTreeNode[]) => {
        nodes.sort((a, b) => {
            if (a.isFolder !== b.isFolder) return a.isFolder ? -1 : 1;
            return a.name.localeCompare(b.name);
        });
        nodes.forEach(n => sortNodes(n.children));
    };
    sortNodes(root.children);

    return root.children;
}

export function hasFileInPath(prefix: string, existingPaths: string[], pendingFiles: QEFile[]): boolean {
    const normalizedPrefix = prefix.endsWith('/') ? prefix : `${prefix}/`;
    return existingPaths.some(p => p.startsWith(normalizedPrefix))
        || pendingFiles.some(f => f.path?.startsWith(normalizedPrefix));
}

export function stripBundleRoot(paths: string[]): string[] {
    if (paths.length === 0) return paths;
    const firstSegment = paths[0].split('/')[0];
    const isFolder = !(/\.[a-zA-Z0-9]+$/.test(firstSegment));
    const allShareRoot = isFolder && paths.every(f => f.startsWith(`${firstSegment}/`));
    if (!allShareRoot) return paths;
    const prefix = `${firstSegment}/`;
    return paths.map(f => f.slice(prefix.length));
}

export function isFileByName(name: string): boolean {
    return /\.[a-zA-Z0-9]+$/.test(name);
}